import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import _ from 'lodash';
import { ApiService, DeviceService, Store, NotificationService } from 'src/data/services/Services';
import type { ApiNotification } from 'src/core/models/api/ApiNotification';
import Constants from 'src/core/utils/Constants';
import type { PushMessageHandler, PushUnsubscribe } from 'src/data/services/types/NotificationServiceTypes';

export type PushNotification = ApiNotification;

interface NotificationState {
	token: string | null;
	notifications: ApiNotification[];
	isLoading: boolean;
	notificationsRead: (string | number)[];
	// Comma-separated uuids of the subscribed events, mirrored by useEventStore on every change. Kept here so this
	// store never has to import useEventStore (require cycle).
	eventUuids: string;
	// Read-state snapshot taken when the messages tab opens: drives the read styling so newly-arrived messages
	// still look unread during a visit even though the badge clears.
	seenAtOpen: (string | number)[];
}

const initialState: NotificationState = {
	token: null,
	notifications: [],
	isLoading: true,
	notificationsRead: [],
	eventUuids: '',
	seenAtOpen: [],
};

async function fetchNotifications(
	token: string | null,
	eventUuids: string,
	set: (partial: Partial<NotificationState>) => void,
	noAnimation: boolean,
) {
	if (!noAnimation) set({ isLoading: true });
	if (!token) {
		set({ isLoading: false });
		return;
	}
	try {
		const { data } = await ApiService.endpoints.notifications.getByToken({ pushToken: token });
		const activeEvents = eventUuids ? eventUuids.split(',') : [];
		const filtered = _.filter(data, (msg) => _.includes(activeEvents, msg.event_uuid));
		const sorted = _.orderBy(filtered, ['execute_time'], ['desc']);
		set({ notifications: sorted, isLoading: false });
	} catch (error) {
		set({ isLoading: false });
		console.error(error);
	}
}

export const useNotificationStore = create(
	combine(initialState, (set, get) => ({
		init: async () => {
			const token = (await NotificationService.getToken()) ?? null;
			set({ token });

			await fetchNotifications(token, get().eventUuids, set, false);

			const savedNotifications = await Store.getObject<(string | number)[]>('notificationsRead');
			if (savedNotifications) {
				set({ notificationsRead: savedNotifications });
			}
		},

		// Canonical per-install registration: sends the current token, device metadata and the full set of
		// subscribed events to the backend.
		registerDevice: async () => {
			const token = await NotificationService.getToken();
			if (!token) return;

			set({ token });

			const eventUuids = get().eventUuids.split(',').filter(Boolean);

			await ApiService.endpoints.mobileDevices.register({
				pushToken: token,
				eventUuids,
				...DeviceService.getMetadata(),
			});
		},

		opened: (id: string | number) => {
			const { notificationsRead } = get();
			if (!notificationsRead.includes(id)) {
				const updated = [...notificationsRead, id];
				set({ notificationsRead: updated });
				Store.save('notificationsRead', updated);
			}
		},

		allOpened: () => {
			const allIds = get().notifications.map((n) => n.id);
			set({ notificationsRead: allIds });
			Store.save('notificationsRead', allIds);
		},

		resetOpened: () => {
			set({ notificationsRead: [] });
			Store.save('notificationsRead', []);
		},

		getNotificationsByToken: async (noAnimation = false) => {
			await fetchNotifications(get().token, get().eventUuids, set, noAnimation);
		},

		// Opening the messages tab: snapshot which messages were already read (so newly- arrived ones still look
		// unread this visit), refresh the list and clear the badge.
		openMessages: async () => {
			set({ seenAtOpen: [...get().notificationsRead] });
			// Sibling actions go via getState(): combine() types `get` as state-only, without the actions.
			try {
				await useNotificationStore.getState().getNotificationsByToken();
			} finally {
				useNotificationStore.getState().allOpened();
			}
		},

		// Pull-to-refresh: refresh, mark all read and fade them (snapshot = all read).
		refreshMessages: async () => {
			try {
				await useNotificationStore.getState().getNotificationsByToken(true);
			} finally {
				useNotificationStore.getState().allOpened();
				set({ seenAtOpen: [...get().notificationsRead] });
			}
		},

		getUnreadNotifications: () => {
			const { notifications, notificationsRead } = get();
			return notifications.filter((n) => !notificationsRead.includes(n.id)).length;
		},

		// Current device push token (or null), so screens never call NotificationService directly.
		getPushToken: async () => (await NotificationService.getToken()) ?? null,

		// The three push listeners, for the same reason as getPushToken: presentation may not
		// reach into the data layer, and routing them here keeps the messaging SDK in one place
		// so the web build can swap the whole service for a no-op sibling.
		onForegroundMessage: (handler: PushMessageHandler): PushUnsubscribe =>
			NotificationService.onForegroundMessage(handler),

		onNotificationOpened: (handler: PushMessageHandler): PushUnsubscribe =>
			NotificationService.onNotificationOpened(handler),

		getInitialNotification: async () => await NotificationService.getInitialNotification(),

		// Persisted enable/disable preference for the notifications toggle.
		getStoredNotificationState: async () => await Store.get(Constants.storageNotificationState),

		// Persists the toggle preference and tells the backend to (de)register this device's pushes.
		setPushEnabled: async (enabled: boolean, pushToken: string) => {
			Store.save(Constants.storageNotificationState, enabled ? 'enable' : 'disable');
			const action = enabled ? ApiService.endpoints.push.enable : ApiService.endpoints.push.disable;
			await action({ pushToken });
		},
	})),
);
